"use client";
// ────────── V3.1 — Recommendation cards (5 profils) ──────────
// Fixes Codex V3.0 :
// - P2 #5 : icône euro mappée
// - P2 #6 : mode mixte signalé clairement (badge + double icône)
// - P2 #7 : friction_severity et prix_type propagés depuis le backend
// - P3 #13 : CTA hébergement quand bookingUrlHeb présent
// - P3 #14 : aria-label sur les liens et carousel role/aria
import React from 'react';
import { VColor, VFont } from './tokens';
import { Card, Eyebrow, FrictionBadge, PrixTypeBadge } from './ui-parts';
import { IconStar, IconClock, IconHeart, IconShield, IconPlane, IconTrain } from './icons';

export interface Recommendation {
  profil: string;
  label: string;
  icon: string;
  transportResume: string;
  transportPrix: number | null;
  hebergementNom: string;
  hebergementPrixPP: number | null;
  totalParPersonne: number | null;
  dureeAllerMin: number | null;
  dureeTotalMin: number | null;       // V3.1 P2 #10
  explication: string;
  modePrincipal: string;               // "avion" | "train" | "mixte"
  frictionLabel: string;
  frictionSeverity: string;
  frictionDetail: string;              // V4.0 Phase 2 Chantier 3 : phrase explicative
  prixType: string;
  bookingUrlTransport?: string;
  bookingUrlHeb?: string;
}

// V3.1 P2 #5 : icône euro intégrée au mapping
const EuroIcon: React.FC<{ size?: number; style?: React.CSSProperties }> = ({ size = 14, style }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: size, height: size, fontFamily: VFont.serif,
    fontSize: Math.round(size * 0.95), fontWeight: 600, lineHeight: 1, ...style,
  }}>{'€'}</span>
);

const ICON_MAP: Record<string, React.FC<{ size?: number; style?: React.CSSProperties }>> = {
  star: IconStar,
  euro: EuroIcon,
  clock: IconClock,
  heart: IconHeart,
  shield: IconShield,
};

// V3.1 P2 #6 : icône combinée pour le mode mixte (train + avion)
const MultimodalIcon: React.FC<{ size?: number; style?: React.CSSProperties }> = ({ size = 14, style }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, ...style }}>
    <IconTrain size={size} />
    <span style={{ fontSize: size * 0.6, color: VColor.muted }}>{'+'}</span>
    <IconPlane size={size} />
  </span>
);

function formatDuree(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${m}min`;
}

function severityFromLabel(label: string): string {
  // Fallback uniquement si le backend n'a pas envoyé friction_severity
  if (label === 'Fluide') return 'good';
  if (label === 'Correct') return 'muted';
  if (label === 'Risqué' || label === 'Fatigant') return 'bad';
  return 'warn';
}

const RecommendationCard: React.FC<{
  reco: Recommendation;
  isHero?: boolean;
}> = ({ reco, isHero = false }) => {
  const IconComp = ICON_MAP[reco.icon] || IconStar;
  // V3.1 P2 #6 : mode mixte affiche les deux icônes au lieu de retomber sur avion
  const ModeIcon = reco.modePrincipal === 'train' ? IconTrain
    : reco.modePrincipal === 'mixte' ? MultimodalIcon
    : IconPlane;
  const modeLabel = reco.modePrincipal === 'train' ? 'Train'
    : reco.modePrincipal === 'mixte' ? 'Train + avion'
    : 'Avion';
  const severity = reco.frictionSeverity || severityFromLabel(reco.frictionLabel);

  return (
    <Card hero={isHero} style={{
      minWidth: 200,
      flex: '1 1 0',
      scrollSnapAlign: 'start',
    }}>
      <div style={{ padding: isHero ? '16px 18px 14px' : '14px 16px 12px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <IconComp size={14} style={{ color: isHero ? VColor.accent : VColor.muted }} />
          <Eyebrow color={isHero ? VColor.accent : VColor.muted}>{reco.label}</Eyebrow>
        </div>

        {/* Prix principal — total /pers prioritaire, transport seul sinon */}
        {reco.totalParPersonne != null ? (
          <div style={{
            fontFamily: VFont.serif, fontSize: isHero ? 32 : 26, fontWeight: 400,
            color: VColor.ink, lineHeight: 1, marginBottom: 4,
          }}>
            {reco.totalParPersonne}
            <span style={{ fontSize: isHero ? 16 : 14, color: VColor.muted }}>{'€'}/pers</span>
          </div>
        ) : reco.transportPrix != null ? (
          <div style={{
            fontFamily: VFont.serif, fontSize: isHero ? 32 : 26, fontWeight: 400,
            color: VColor.ink, lineHeight: 1, marginBottom: 4,
          }}>
            {reco.transportPrix}
            <span style={{ fontSize: isHero ? 16 : 14, color: VColor.muted }}>{'€'}/pers</span>
          </div>
        ) : (
          <div style={{
            fontFamily: VFont.sans, fontSize: 13, color: VColor.muted, fontStyle: 'italic',
            lineHeight: 1, marginBottom: 4, marginTop: 6,
          }}>Prix indisponible</div>
        )}

        {/* V3.1 P2 #7 : badge type de prix sous le prix */}
        {(reco.transportPrix != null || reco.totalParPersonne != null) && (
          <div style={{ marginTop: 4 }}>
            <PrixTypeBadge type={reco.prixType} />
          </div>
        )}

        {/* Transport + mode label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
          <ModeIcon size={14} style={{ color: VColor.ink2 }} />
          <span style={{
            fontFamily: VFont.sans, fontSize: 13, color: VColor.ink2,
            fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}>{reco.transportResume}</span>
        </div>

        {/* Mode label sous-titre quand mixte */}
        {reco.modePrincipal === 'mixte' && (
          <div style={{ fontFamily: VFont.mono, fontSize: 9, color: VColor.muted, marginTop: 2, letterSpacing: 0.4, textTransform: 'uppercase' as const }}>
            {modeLabel}
          </div>
        )}

        {/* V3.1 P2 #10 : durée selon profil — fastest montre aller+retour, autres montrent aller */}
        {(reco.profil === 'fastest' && reco.dureeTotalMin != null) ? (
          <div style={{
            fontFamily: VFont.mono, fontSize: 10, color: VColor.accent,
            marginTop: 4, letterSpacing: 0.3,
          }}>
            {formatDuree(reco.dureeTotalMin)} aller+retour
          </div>
        ) : reco.dureeAllerMin != null ? (
          <div style={{
            fontFamily: VFont.mono, fontSize: 10, color: VColor.accent,
            marginTop: 4, letterSpacing: 0.3,
          }}>
            {formatDuree(reco.dureeAllerMin)} aller
          </div>
        ) : null}

        {/* Hébergement */}
        {reco.hebergementNom && (
          <div style={{
            fontFamily: VFont.sans, fontSize: 12, color: VColor.muted, marginTop: 6,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {reco.hebergementNom}
            {reco.hebergementPrixPP != null && (
              <span style={{ color: VColor.ink2 }}>{' · '}{reco.hebergementPrixPP}{'€'}/pers</span>
            )}
          </div>
        )}

        {/* Explication + friction detail */}
        <div style={{
          fontFamily: VFont.sans, fontSize: 12, color: VColor.muted,
          fontStyle: 'italic', marginTop: 8, lineHeight: 1.35,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
        }}>
          {reco.explication}
          {reco.frictionDetail && reco.frictionDetail !== reco.explication && (
            <span style={{ color: VColor.ink2 }}>{' — '}{reco.frictionDetail}</span>
          )}
        </div>

        {/* Friction + CTAs */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, gap: 6 }}>
          <FrictionBadge label={reco.frictionLabel} severity={severity} />
          <div style={{ display: 'flex', gap: 10 }}>
            {reco.bookingUrlTransport && (
              <a
                href={reco.bookingUrlTransport}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Voir le ${modeLabel.toLowerCase()} pour ${reco.label}`}
                style={{
                  fontFamily: VFont.sans, fontSize: 12, fontWeight: 600,
                  color: VColor.accent, textDecoration: 'none',
                }}
              >
                {modeLabel === 'Train' ? 'Train' : modeLabel === 'Avion' ? 'Vol' : 'Trajet'} {'→'}
              </a>
            )}
            {/* V3.1 P3 #13 : CTA hébergement explicite quand le total inclut un hôtel */}
            {reco.bookingUrlHeb && reco.hebergementNom && (
              <a
                href={reco.bookingUrlHeb}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Réserver l'hébergement ${reco.hebergementNom}`}
                style={{
                  fontFamily: VFont.sans, fontSize: 12, fontWeight: 600,
                  color: VColor.accent, textDecoration: 'none',
                }}
              >
                {"Hébergement →"}
              </a>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export const RecommendationCards: React.FC<{
  recommendations: Recommendation[];
}> = ({ recommendations }) => {
  if (!recommendations || recommendations.length === 0) return null;

  return (
    <div style={{ padding: '0 12px' }}>
      <Eyebrow color={VColor.ink2}>Nos recommandations</Eyebrow>
      <div style={{ height: 8 }} />
      <div
        role="region"
        aria-label="Recommandations de voyage"
        tabIndex={0}
        style={{
          display: 'flex', gap: 10, overflowX: 'auto', scrollSnapType: 'x mandatory',
          paddingBottom: 8, WebkitOverflowScrolling: 'touch',
          msOverflowStyle: 'none', scrollbarWidth: 'none',
          outline: 'none',
        }}
      >
        {recommendations.map((r) => (
          <RecommendationCard
            key={r.profil}
            reco={r}
            isHero={r.profil === 'best-value'}
          />
        ))}
      </div>
    </div>
  );
};
